/**
 * Centralized API utility for making authenticated requests.
 * Use this instead of raw fetch() to ensure consistent auth and tenant headers.
 * Includes automatic token refresh on 401 errors.
 */
import { isPublicPath } from '@/utils/publicPaths';

// Use environment variable for API base URL, fallback to localhost for development
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';
export const API_BASE = `${API_BASE_URL}/api`;

/**
 * Error thrown for a non-2xx answer: the message for the user, plus the HTTP
 * status and the parsed body so a screen can react to a structured refusal
 * (e.g. a 409 carrying a `code` and the records it concerns).
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly data: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Token refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Get authentication headers from localStorage.
 * Includes Authorization token and X-Tenant-ID.
 */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = localStorage.getItem('nexedu_access_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const userStr = localStorage.getItem('nexedu_user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user?.tenantId) {
        headers['X-Tenant-ID'] = user.tenantId;
      }
    } catch {
      // Ignore JSON parse errors
    }
  }

  return headers;
}

/**
 * Get headers for multipart/form-data requests.
 * Does NOT include Content-Type (browser sets it with boundary).
 */
export function getMultipartAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  const token = localStorage.getItem('nexedu_access_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const userStr = localStorage.getItem('nexedu_user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user?.tenantId) {
        headers['X-Tenant-ID'] = user.tenantId;
      }
    } catch {
      // Ignore JSON parse errors
    }
  }

  return headers;
}

/**
 * Attempt to refresh the access token using the refresh token.
 * Returns true if refresh was successful.
 */
async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem('nexedu_refresh_token');
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();

    // Update tokens in localStorage
    localStorage.setItem('nexedu_access_token', data.accessToken);
    localStorage.setItem('nexedu_refresh_token', data.refreshToken);
    // Let the auth store (and ProtectedRoute) pick up the new token.
    window.dispatchEvent(new CustomEvent('nexedu:token-refreshed'));

    return true;
  } catch {
    return false;
  }
}

/**
 * Clear auth state and redirect to login.
 *
 * Public pages (marketing, password-reset, etc.) are excluded: a stale
 * token in localStorage would otherwise hijack the recovery flow or
 * bounce anonymous visitors of the marketing site back to /login.
 */
function clearAuthAndRedirect(): void {
  localStorage.removeItem('nexedu_access_token');
  localStorage.removeItem('nexedu_refresh_token');
  localStorage.removeItem('nexedu_user');
  localStorage.removeItem('nexedu-auth-storage');

  if (!isPublicPath(window.location.pathname)) {
    window.location.href = '/login';
  }
}

/**
 * Handle API response and check for errors.
 * Attempts token refresh on 401 before giving up.
 */
export async function handleResponse<T>(
  response: Response,
  retryFn?: () => Promise<Response>
): Promise<T> {
  if (response.status === 401 && retryFn) {
    // Token may be expired, try to refresh
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = tryRefreshToken();
    }

    // Wait for the refresh to complete
    const refreshSuccess = await refreshPromise;
    isRefreshing = false;
    refreshPromise = null;

    if (refreshSuccess) {
      // Retry the original request with new token
      const retryResponse = await retryFn();
      return handleResponse<T>(retryResponse);
    } else {
      // Refresh failed, redirect to login
      clearAuthAndRedirect();
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }
  }

  if (response.status === 401) {
    // No retry function or already retried, redirect to login
    clearAuthAndRedirect();
    throw new Error('Session expirée. Veuillez vous reconnecter.');
  }

  // Handle 403 Financial Suspension
  if (response.status === 403) {
    const errorData = await response.json().catch(() => ({ message: '' }));
    const errorMessage = errorData.message || '';

    // Check if this is a financial suspension error
    if (errorMessage.includes('suspendu pour raison financ') ||
        errorMessage.includes('suspendu pour raison financière')) {
      // Save suspension state to localStorage
      localStorage.setItem('nexedu_financial_suspended', 'true');
      localStorage.setItem('nexedu_financial_suspension_message', errorMessage);

      // Redirect to financial suspended page (unless already there)
      if (window.location.pathname !== '/financial-suspended') {
        window.location.href = '/financial-suspended';
      }
    }

    // Keep the structured refusal (code, records concerned) for screens that
    // explain it in place, e.g. a protected session in the planning dialog
    throw new ApiError(errorMessage || 'Accès refusé', 403, errorData);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Une erreur est survenue' }));
    throw new ApiError(error.message || `Erreur ${response.status}`, response.status, error);
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

/**
 * Run a fetch and turn network-level failures (the browser's raw
 * "Failed to fetch" TypeError, thrown when the server is momentarily
 * unreachable — cold start, brief connectivity loss, mid-deploy) into a clear
 * French message. Idempotent reads may be retried a few times so these blips
 * self-heal without the user having to refresh. Writes are NEVER auto-retried,
 * to avoid duplicating an action that may already have reached the server.
 */
async function fetchSafe(makeRequest: () => Promise<Response>, retries = 0): Promise<Response> {
  const baseDelay = 500;
  for (let attempt = 0; ; attempt++) {
    try {
      return await makeRequest();
    } catch (err) {
      // Native fetch throws a TypeError only for network-level failures.
      const isNetworkError = err instanceof TypeError;
      if (isNetworkError && attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, baseDelay * (attempt + 1)));
        continue;
      }
      if (isNetworkError) {
        throw new Error('Connexion au serveur interrompue. Vérifiez votre connexion Internet, puis réessayez.');
      }
      throw err;
    }
  }
}

/**
 * Refresh-aware fetch. Performs the request with auth (and tenant) headers and,
 * on a 401, transparently refreshes the access token once (single-flight) and
 * replays the request with the fresh token. Returns the raw Response so callers
 * can inspect `.ok` / `.status` / `.json()` / `.blob()` as usual. Only when the
 * refresh itself fails do we clear the session and bounce to /login.
 *
 * Use this instead of raw `fetch()` on authenticated screens so a 15-minute
 * token expiry self-heals instead of surfacing "jwt expired" + a Réessayer card.
 *
 * `input` may be a full URL or a path (prefixed with API_BASE).
 */
export async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const url = /^https?:\/\//.test(input)
    ? input
    : `${API_BASE}${input.startsWith('/') ? input : `/${input}`}`;
  const isMultipart = init.body instanceof FormData;

  const buildInit = (): RequestInit => ({
    ...init,
    headers: {
      // Caller headers first, then our fresh auth headers LAST so a stale
      // Authorization token passed by the caller is always overridden (and the
      // replay after a refresh picks up the new token).
      ...((init.headers as Record<string, string> | undefined) ?? {}),
      ...(isMultipart ? getMultipartAuthHeaders() : getAuthHeaders()),
    },
  });

  let response = await fetch(url, buildInit());

  if (response.status === 401) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = tryRefreshToken();
    }
    const refreshed = await refreshPromise;
    isRefreshing = false;
    refreshPromise = null;

    if (refreshed) {
      response = await fetch(url, buildInit()); // replay with the new token
    } else {
      clearAuthAndRedirect();
    }
  }

  return response;
}

/**
 * Make an authenticated GET request.
 */
export async function apiGet<T>(endpoint: string): Promise<T> {
  const makeRequest = () => fetch(`${API_BASE}${endpoint}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  // Reads are safe to retry on a transient network blip. 3 retries with
  // growing backoff (~3s total) also covers a Neon compute resume.
  const response = await fetchSafe(makeRequest, 3);
  return handleResponse<T>(response, makeRequest);
}

/**
 * Make an authenticated POST request.
 */
export async function apiPost<T>(endpoint: string, data?: unknown): Promise<T> {
  const makeRequest = () => fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: data ? JSON.stringify(data) : undefined,
  });

  const response = await fetchSafe(makeRequest);
  return handleResponse<T>(response, makeRequest);
}

/**
 * Make an authenticated PUT request.
 */
export async function apiPut<T>(endpoint: string, data?: unknown): Promise<T> {
  const makeRequest = () => fetch(`${API_BASE}${endpoint}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data ? JSON.stringify(data) : undefined,
  });

  const response = await fetchSafe(makeRequest);
  return handleResponse<T>(response, makeRequest);
}

/**
 * Make an authenticated PATCH request.
 */
export async function apiPatch<T>(endpoint: string, data?: unknown): Promise<T> {
  const makeRequest = () => fetch(`${API_BASE}${endpoint}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: data ? JSON.stringify(data) : undefined,
  });

  const response = await fetchSafe(makeRequest);
  return handleResponse<T>(response, makeRequest);
}

/**
 * Make an authenticated DELETE request.
 */
export async function apiDelete<T>(endpoint: string): Promise<T> {
  const makeRequest = () => fetch(`${API_BASE}${endpoint}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  const response = await fetchSafe(makeRequest);
  return handleResponse<T>(response, makeRequest);
}

/**
 * Make an authenticated DELETE request with a JSON body
 * (e.g. soft-delete that requires a motif).
 */
export async function apiDeleteWithBody<T>(endpoint: string, data: unknown): Promise<T> {
  const makeRequest = () => fetch(`${API_BASE}${endpoint}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  const response = await fetchSafe(makeRequest);
  return handleResponse<T>(response, makeRequest);
}

/**
 * Make an authenticated multipart POST request (for file uploads).
 */
export async function apiPostMultipart<T>(endpoint: string, formData: FormData): Promise<T> {
  const makeRequest = () => fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: getMultipartAuthHeaders(),
    body: formData,
  });

  const response = await fetchSafe(makeRequest);
  return handleResponse<T>(response, makeRequest);
}

/**
 * Fetch a file as a blob with authentication headers.
 * Use this for secure file downloads instead of passing tokens in URLs.
 */
export async function apiFetchBlob(endpoint: string): Promise<Blob> {
  const headers = getAuthHeaders();
  // Remove Content-Type for blob requests
  delete headers['Content-Type'];

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'GET',
    headers,
  });

  if (response.status === 401) {
    // Try to refresh token
    const refreshSuccess = await tryRefreshToken();
    if (refreshSuccess) {
      // Retry with new token
      const newHeaders = getAuthHeaders();
      delete newHeaders['Content-Type'];
      const retryResponse = await fetch(`${API_BASE}${endpoint}`, {
        method: 'GET',
        headers: newHeaders,
      });
      if (!retryResponse.ok) {
        throw new Error(`Erreur ${retryResponse.status}`);
      }
      return retryResponse.blob();
    } else {
      clearAuthAndRedirect();
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }
  }

  if (!response.ok) {
    throw new Error(`Erreur ${response.status}`);
  }

  return response.blob();
}

/**
 * Download a file securely using Authorization header instead of URL token.
 * Creates a blob URL and triggers download.
 */
export async function apiDownloadFile(endpoint: string, filename: string): Promise<void> {
  const blob = await apiFetchBlob(endpoint);
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up blob URL after a short delay
  setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
}
